import { Color } from "@repo/app-types/constants/colors";
import type { VocalIntelligence } from "@repo/app-types/entities/vocalIntelligence";
import { forwardRef, useMemo, useRef, type FC, type ReactNode } from "react";
import { Heading } from "react-aria-components";
import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  useChartHeight,
  type ActiveDotProps,
  type DotItemDotProps,
} from "recharts";
import { AnchoredTooltip } from "../tooltip/anchoredTooltip.js";
import { InfoIconTooltip } from "../tooltip/infoIconTooltip.js";

export const vocalIntelligenceDimensionLabels = {
  authority: "Confidence",
  energy: "Energy",
  rapport: "Rapport",
} as const satisfies Record<keyof VocalIntelligence, string>;
const dimensionKeys = Object.keys(
  vocalIntelligenceDimensionLabels,
) as readonly (keyof VocalIntelligence)[];

interface RadarPoint {
  readonly theme: string;
  readonly score: number;
  readonly commentary: string;
}

type VocalScoreColorTier =
  | typeof Color.Red
  | typeof Color.Amber
  | typeof Color.Yellow
  | typeof Color.Lime
  | typeof Color.Green;

export function mapVocalScoreToTenPointScale(score: number): number {
  return 1 + ((score - 1) * 9) / 4;
}

export function getVocalScoreColorTier(score: number): VocalScoreColorTier {
  if (score <= mapVocalScoreToTenPointScale(1.5)) return Color.Red;
  if (score <= mapVocalScoreToTenPointScale(2.5)) return Color.Amber;
  if (score <= mapVocalScoreToTenPointScale(3.5)) return Color.Yellow;
  if (score <= mapVocalScoreToTenPointScale(4.5)) return Color.Lime;
  return Color.Green;
}

function formatVocalScore(score: number): string {
  return Number.isInteger(score)
    ? String(score)
    : score.toFixed(1).replace(/\.0$/, "");
}

const VOCAL_SCORE_DOT_COLORS: Record<VocalScoreColorTier, string> = {
  [Color.Red]: "var(--color-red-400)",
  [Color.Amber]: "var(--color-amber-400)",
  [Color.Yellow]: "var(--color-yellow-400)",
  [Color.Lime]: "var(--color-lime-400)",
  [Color.Green]: "var(--color-green-400)",
};

function getVocalScoreDotColor(score: number): string {
  return VOCAL_SCORE_DOT_COLORS[getVocalScoreColorTier(score)];
}

function renderVocalScoreDot(props: DotItemDotProps): ReactNode {
  if (props.cx === undefined || props.cy === undefined) {
    return null;
  }

  const payload = props.payload as RadarPoint;
  return <VocalScoreDot cx={props.cx} cy={props.cy} score={payload.score} />;
}

function renderActiveVocalScoreDot(props: ActiveDotProps): ReactNode {
  if (props.cx === undefined || props.cy === undefined) {
    return null;
  }

  return (
    <ActiveVocalScoreDot
      index={props.index}
      cx={props.cx}
      cy={props.cy}
      payload={props.payload}
    />
  );
}

// Shared component for active and inactive dots.
const VocalScoreDot = forwardRef<
  SVGCircleElement,
  { cx: number; cy: number; score: number; isActive?: boolean }
>((props, ref) => {
  return (
    <circle
      ref={ref}
      cx={props.cx}
      cy={props.cy}
      r={props.isActive ? 5 : 3}
      fill={getVocalScoreDotColor(props.score)}
    />
  );
});

// Renders the current active dot. Rendered by Rechart in a layer on top of the inactive variation of the dot, and also reused when the active dot changes.
const ActiveVocalScoreDot = (props: {
  index: number;
  cx: number;
  cy: number;
  payload: RadarPoint;
}) => {
  const triggerRef = useRef<SVGCircleElement | null>(null);
  const chartHeight = useChartHeight();

  return (
    <>
      <VocalScoreDot
        ref={triggerRef}
        cx={props.cx}
        cy={props.cy}
        score={props.payload.score}
        isActive
      />
      <AnchoredTooltip
        key={props.index}
        triggerRef={triggerRef}
        placement={
          // Push the tooltip away from the middle of the chart so it doesn't
          // obscure it: above a dot in the top half, below one in the bottom.
          chartHeight !== undefined && props.cy < chartHeight / 2
            ? "top"
            : "bottom"
        }
      >
        <div className="mb-1 font-medium">
          {props.payload.theme}: {formatVocalScore(props.payload.score)}/10
        </div>
        <div>{props.payload.commentary}</div>
      </AnchoredTooltip>
    </>
  );
};

type VocalIntelligenceRadarChartProps = {
  readonly vocalScore: VocalIntelligence;
};

export const VocalIntelligenceRadarChart: FC<
  VocalIntelligenceRadarChartProps
> = ({ vocalScore }) => {
  const data: readonly RadarPoint[] = useMemo(() => {
    return dimensionKeys.map((key) => ({
      theme: vocalIntelligenceDimensionLabels[key],
      score: mapVocalScoreToTenPointScale(vocalScore[key].score),
      commentary: vocalScore[key].commentary,
    }));
  }, [vocalScore]);

  return (
    <section className="group space-y-2">
      <div className="flex items-center gap-1.5">
        <Heading level={2} className="text-base font-semibold text-foreground">
          Vocal intelligence
        </Heading>
        <InfoIconTooltip className="opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
          An overall score for vocal qualities like confidence, energy, and
          rapport, calculated each week from a sample of the teammates&apos;
          calls.
        </InfoIconTooltip>
      </div>
      <div className="h-[180px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={[...data]} outerRadius="70%">
            <PolarGrid stroke="#E5E7EB" radialLines={false} />
            <PolarAngleAxis
              dataKey="theme"
              tick={{ fill: "#374151", fontSize: 12 }}
            />
            <PolarRadiusAxis
              angle={90}
              domain={[0, 10]}
              ticks={[2, 4, 6, 8, 10]}
              tick={false}
              axisLine={false}
            />
            <Radar
              name="Vocal score"
              dataKey="score"
              stroke="#3B82F6"
              fill="#3B82F6"
              fillOpacity={0.3}
              strokeWidth={1}
              dot={renderVocalScoreDot}
              activeDot={renderActiveVocalScoreDot}
              // The default animations for radar charts are dumb
              isAnimationActive={false}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
};
